import ApiService from "../../../core/services/ApiService";

// ─── Endpoint map (all now implemented on the backend) ───────────────────────
// GET    /api/entity?clientId={guid}   — list for a client
// GET    /api/entity/:id               — single entity by id
// POST   /api/entity                   — create
// PUT    /api/entity/:id               — update
// DELETE /api/entity/:id               — deactivate (soft delete)
// ─────────────────────────────────────────────────────────────────────────────

export interface EntityDto {
  id: number;
  name: string;
  orgId?: string;            // legacy field from dropdown endpoint; not in new EntityDto
  entityType: string;        // "Partnership" | "Partner"
  category: string;          // "Limited Partnership" | "Individual" | etc.
  einSsn: string;            // masked: "••••6789" or "12-3456789"
  einSsnFull?: string;       // unmasked — only present for authorised callers
  status: string;            // "Active" | "Inactive"
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  taxYearStart?: string;
  clientId?: string;
  clientName?: string;
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateEntityDto {
  name: string;
  entityType: string;
  category?: string;
  einSsn?: string;
  address?: string;
  city?: string;
  state?: string;
  zipCode?: string;
  taxYearStart?: string;
  clientId?: string;
  notes?: string;
}

export interface UpdateEntityDto extends Partial<CreateEntityDto> {}

class EntityService extends ApiService {
  // Legacy dropdown endpoint — kept for backward compat with other callers
  getLegacyEntities(): Promise<{ id: number; name: string; orgId: string }[]> {
    return this.makeRequest<{ id: number; name: string; orgId: string }[]>(
      "/api/project/entities",
      { extractData: true },
    );
  }

  getEntitiesByClient(clientId: string): Promise<EntityDto[]> {
    return this.makeRequest<EntityDto[]>(
      `/api/entity?clientId=${clientId}`,
      { extractData: true },
    );
  }

  getEntityById(id: number): Promise<EntityDto> {
    return this.makeRequest<EntityDto>(
      `/api/entity/${id}`,
      { extractData: true },
    );
  }

  createEntity(dto: CreateEntityDto): Promise<EntityDto> {
    return this.makeRequest<EntityDto>(
      "/api/entity",
      { method: "post", data: dto as unknown as Record<string, unknown>, extractData: true },
    );
  }

  updateEntity(id: number, dto: UpdateEntityDto): Promise<EntityDto> {
    return this.makeRequest<EntityDto>(
      `/api/entity/${id}`,
      { method: "put", data: dto as unknown as Record<string, unknown>, extractData: true },
    );
  }

  deactivateEntity(id: number): Promise<void> {
    return this.makeRequest<void>(
      `/api/entity/${id}`,
      { method: "delete", extractData: true },
    );
  }
}

const entityService = new EntityService();
export default entityService;
